import { create } from "zustand";
import { COMMUNITY_TIMEOUT_MILLIS, WEEKLY_CAMPAIGN_KEY } from "../constants/CommunityConstants";
import { jqXHR, ResponseError } from "../types/Api";
import { CampaignConfig } from "../types/Campaign";
import {
  CommunicationStatus,
  CommunicationsResponse,
  AppInsight,
  SignUpStatsResponse,
  TakeUpData,
  TopAppsResponse,
  TopArticlesResponse,
} from "../types/Community";
import { DeviceOptions } from "../types/Device";
import { useSessionStore } from "./SessionStore";
import Api from "../utils/Api";
import { getSignaturesAndKeywords } from "../pages/filtering/ApiHelpers";

export type AsyncStatus = "idle" | "loading" | "success" | "error";

interface AsyncData<T> {
  data: T;
  status: AsyncStatus;
  error?: string;
}

export interface TopAppViewItem extends AppInsight {
  name: string;
  faviconUrl: string;
}

export interface TopAppsViewResponse {
  data: TopAppViewItem[];
}

export interface CommunityDashboardStore {
  takeUp: AsyncData<TakeUpData>;
  signUps: AsyncData<SignUpStatsResponse>;
  signUpsDownloadStatus: AsyncStatus;
  communications: AsyncData<CommunicationsResponse>;
  topApps: AsyncData<TopAppsViewResponse>;
  topArticles: AsyncData<TopArticlesResponse>;

  fetchTakeUp: (options?: InsightsUrlOptions) => Promise<void>;
  fetchSignUps: () => Promise<void>;
  fetchAllSignUps: () => Promise<SignUpStatsResponse>;
  fetchCommunications: () => Promise<void>;
  fetchTopAppsWithIcons: () => Promise<void>;
  fetchTopArticles: () => Promise<void>;

  reset: () => void;
  resetTakeUp: () => void;
}

const DEFAULT_SIGN_UPS: SignUpStatsResponse = { data: [] };
const DEFAULT_COMMUNICATIONS: CommunicationsResponse = { data: [] };
const DEFAULT_TOP_APPS: TopAppsViewResponse = { data: [] };
const DEFAULT_TOP_ARTICLES: TopArticlesResponse = { topArticles: [], safetyHubUrl: "" };
const DEFAULT_TAKE_UP: TakeUpData = {
  users: { linkedCount: 0, claimedCount: 0 },
  guardians: { linkedCount: 0, claimedCount: 0 },
};

function initial<T>(data: T): AsyncData<T> {
  return {
    data,
    status: "idle",
    error: undefined,
  };
}

type DataKeys = "takeUp" | "signUps" | "communications" | "topApps" | "topArticles";

type Key = keyof Pick<CommunityDashboardStore, DataKeys>;

type FetchOptions<T> = {
  name: string;
  key: Key;
  path: string;
  defaultData: T;
  options?: InsightsUrlOptions;
  limitDate?: Date;
};

interface FetchInsightsOptions {
  path: string;
  options?: InsightsUrlOptions;
  limitDate?: Date;
}

function getErrorMessage(error: unknown): string {
  return isJqXHR(error) ? `Error ${error.status}` : "Unknown error";
}

function getFallbackAppName(signature: string): string {
  const appName = signature.split(".").pop()?.trim() ?? "";
  if (!appName) {
    return "Unknown App";
  }

  return appName.charAt(0).toUpperCase() + appName.slice(1);
}

async function fetchInsightsData<T>(name: string, { path, options, limitDate }: FetchInsightsOptions): Promise<T> {
  const device = useSessionStore.getState().getDevice();
  const url = getInsightsUrl(device.id, path, { ...options, limitDate });

  try {
    return (await Api.getAsyncWithTimeout(url, COMMUNITY_TIMEOUT_MILLIS)) as T;
  } catch (error) {
    if (!isJqXHR(error)) throw error;
    if (error.status !== 200) {
      throw new ResponseError(`Failed to fetch ${name} data`, error.status);
    }
    throw error;
  }
}

export const useCommunityDashboardStore = create<CommunityDashboardStore>((set) => {
  let cachedSignatures: Array<{ id?: string; name?: string; favicon_url?: string }> = [];

  const setLoadingState = (key: Key) => {
    set((state) => ({
      [key]: { ...state[key], status: "loading", error: undefined },
    }));
  };

  const setErrorState = <T>(key: Key, defaultData: T, error: unknown) => {
    set({
      [key]: { data: defaultData, status: "error", error: getErrorMessage(error) },
    });
  };

  const fetch = async <T>({ name, key, path, defaultData, options, limitDate }: FetchOptions<T>) => {
    setLoadingState(key);

    try {
      const data = await fetchInsightsData<T>(name, { path, options, limitDate });
      set({ [key]: { data, status: "success", error: undefined } });
    } catch (error) {
      setErrorState(key, defaultData, error);
      throw error;
    }
  };

  return {
    takeUp: initial(DEFAULT_TAKE_UP),
    signUps: initial(DEFAULT_SIGN_UPS),
    signUpsDownloadStatus: "idle" as AsyncStatus,
    communications: initial(DEFAULT_COMMUNICATIONS),
    topApps: initial(DEFAULT_TOP_APPS),
    topArticles: initial(DEFAULT_TOP_ARTICLES),

    fetchTakeUp: (options) =>
      fetch<TakeUpData>({
        name: "Take Up",
        key: "takeUp",
        path: "take-up",
        defaultData: DEFAULT_TAKE_UP,
        options,
      }),

    fetchSignUps: () => {
      return fetch<SignUpStatsResponse>({
        name: "Sign-Ups Over Time",
        key: "signUps",
        path: "signup-stats",
        defaultData: DEFAULT_SIGN_UPS,
      });
    },

    fetchAllSignUps: async (): Promise<SignUpStatsResponse> => {
      set({ signUpsDownloadStatus: "loading" });
      try {
        const data = await fetchInsightsData<SignUpStatsResponse>("Sign-Ups for download", { path: "signup-stats" });
        set({ signUpsDownloadStatus: "success" });
        return data;
      } catch (error) {
        set({ signUpsDownloadStatus: "error" });
        throw error;
      }
    },

    fetchCommunications: async () => {
      setLoadingState("communications");

      const device = useSessionStore.getState().getDevice();
      const communicationsUrl = getInsightsUrl(device.id, "communications");
      const weeklyCampaignConfigUrl = `/cadm/v1/appliances/${device.id}/campaigns/${WEEKLY_CAMPAIGN_KEY}/config`;

      try {
        // Fetch both in parallel
        const [communications, weeklyCampaignConfig] = await Promise.all([
          Api.getAsyncWithTimeout(communicationsUrl, COMMUNITY_TIMEOUT_MILLIS) as Promise<CommunicationsResponse>,
          Api.getAsyncWithTimeout(weeklyCampaignConfigUrl, COMMUNITY_TIMEOUT_MILLIS)
            .then((config) => config as CampaignConfig)
            .catch(() => null), // If config fetch fails, we'll default to inactive
        ]);

        // Update the weekly campaign's status based on the config
        const campaignsWithStatus = communications.data.map((campaign) => {
          if (campaign.frequency === "weekly") {
            return {
              ...campaign,
              status: (weeklyCampaignConfig?.enabled ? "active" : "inactive") as CommunicationStatus,
            };
          }
          return campaign;
        });

        set({
          communications: {
            data: { data: campaignsWithStatus },
            status: "success",
            error: undefined,
          },
        });
      } catch (error) {
        setErrorState("communications", DEFAULT_COMMUNICATIONS, error);
        if (!isJqXHR(error)) throw error;
        if (error.status !== 200) {
          throw new ResponseError("Failed to fetch Communications data", error.status);
        }
      }
    },

    fetchTopAppsWithIcons: async () => {
      setLoadingState("topApps");

      try {
        const [{ data: topAppsData }, { signatures }] = await Promise.all([
          fetchInsightsData<TopAppsResponse>("Top 3 App Insights", { path: "top-apps" }),
          getSignaturesAndKeywords(),
        ]);
        const topThreeApps = topAppsData.slice(0, 3);
        const signaturesToUse = signatures.length > 0 ? signatures : cachedSignatures;

        if (signatures.length > 0) {
          cachedSignatures = signatures;
        } else if (cachedSignatures.length > 0) {
          console.warn("Received empty signatures array for Top Apps. Reusing cached signatures metadata.");
        }

        const signatureMap = new Map<string, { name: string; faviconUrl: string }>();
        for (const signature of signaturesToUse) {
          if (signature.id && signature.name) {
            const signatureData = {
              name: signature.name,
              faviconUrl: signature.favicon_url ?? "",
            };
            signatureMap.set(signature.id, signatureData);
          }
        }

        const enrichedTopApps: TopAppViewItem[] = topThreeApps.map((app) => {
          const signatureInfo = signatureMap.get(app.signature);
          if (!signatureInfo) {
            console.warn("Missing Top App signature metadata for app", {
              topApp: app,
              requestedSignatureId: app.signature,
              signatures: signaturesToUse,
            });
            return {
              ...app,
              name: getFallbackAppName(app.signature),
              faviconUrl: "",
            };
          }
          return {
            ...app,
            name: signatureInfo.name,
            faviconUrl: signatureInfo.faviconUrl,
          };
        });

        set({
          topApps: {
            data: { data: enrichedTopApps },
            status: "success",
            error: undefined,
          },
        });
      } catch (error) {
        setErrorState("topApps", DEFAULT_TOP_APPS, error);
        throw error;
      }
    },

    fetchTopArticles: async () => {
      setLoadingState("topArticles");

      try {
        const response = (await Api.getAsyncWithTimeout("/managedevice/ajax/device", COMMUNITY_TIMEOUT_MILLIS)) as {
          data: DeviceOptions;
        };
        const contentHubUrl = response.data?.content_hub_url ?? "";

        if (contentHubUrl === "") {
          set({ topArticles: { data: DEFAULT_TOP_ARTICLES, status: "idle", error: undefined } });
          return;
        }

        const data = await fetchInsightsData<TopArticlesResponse>("Top 3 Hub Articles", {
          path: "top-articles",
          options: { contentHubUrl },
        });
        set({
          topArticles: {
            data: { ...data, safetyHubUrl: contentHubUrl },
            status: "success",
            error: undefined,
          },
        });
      } catch (error) {
        setErrorState("topArticles", DEFAULT_TOP_ARTICLES, error);
        throw error;
      }
    },

    reset: () => {
      set({
        takeUp: initial(DEFAULT_TAKE_UP),
        signUps: initial(DEFAULT_SIGN_UPS),
        signUpsDownloadStatus: "idle",
        communications: initial(DEFAULT_COMMUNICATIONS),
        topApps: initial(DEFAULT_TOP_APPS),
        topArticles: initial(DEFAULT_TOP_ARTICLES),
      });
    },

    resetTakeUp: () => {
      set({ takeUp: initial(DEFAULT_TAKE_UP) });
    },
  };
});

export function baseInsightsUrl(): string {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}`;
}

function isJqXHR(error: unknown): error is jqXHR {
  return typeof error === "object" && error != null && "status" in error;
}

interface InsightsUrlOptions {
  groupId?: string;
  limitDate?: Date;
  contentHubUrl?: string;
}

function getInsightsUrl(deviceId: string, path: string, options?: InsightsUrlOptions): string {
  const url = `/cadm/v1/appliances/${deviceId}/insights/${path}`;
  const params = new URLSearchParams();

  if (options?.groupId?.trim()) {
    params.append("groupId", options.groupId);
  }

  if (options?.limitDate) {
    const formattedDate = options.limitDate.toISOString().split("T")[0];
    params.append("limit_date", formattedDate);
  }
  if (options?.contentHubUrl?.trim()) {
    params.append("contentHubUrl", options.contentHubUrl);
  }

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}
